import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, Send, FileText, AlertTriangle, Clock, KeyRound,
  MessageSquare, UserPlus, UserX, Info, BellOff,
} from 'lucide-react';
import { notificationsApi } from '../../Api/BaseApi';
import './index.css';

const POLL_MS = 45000; // 30–60s polling for the unread count while active

// Map event type -> icon + accent color (drives the colored dot/icon).
const TYPE_META = {
  autosend_complete: { Icon: Send, cls: 'green' },
  report_sent: { Icon: FileText, cls: 'green' },
  query_limit_warning: { Icon: AlertTriangle, cls: 'amber' },
  query_limit_reached: { Icon: AlertTriangle, cls: 'red' },
  access_expiry_warning: { Icon: Clock, cls: 'amber' },
  password_change_required: { Icon: KeyRound, cls: 'blue' },
  chat_summary: { Icon: MessageSquare, cls: 'green' },
  admin_user_created: { Icon: UserPlus, cls: 'blue' },
  admin_user_suspended: { Icon: UserX, cls: 'amber' },
  admin_user_expired: { Icon: Clock, cls: 'red' },
  info: { Icon: Info, cls: 'gray' },
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return `Yesterday at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString();
};

const DISPLAY_LIMIT = 20;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try { setUnread(await notificationsApi.unreadCount()); } catch (e) { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.list(DISPLAY_LIMIT);
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
      setHasMore(!!data.has_more);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  // Initial count + polling while mounted.
  useEffect(() => {
    refreshCount();
    const id = setInterval(() => { open ? loadList() : refreshCount(); }, POLL_MS);
    return () => clearInterval(id);
  }, [open, refreshCount, loadList]);

  // Load the list when the panel opens; move focus into the panel.
  useEffect(() => {
    if (open) {
      loadList();
      // focus the panel for keyboard users
      setTimeout(() => panelRef.current && panelRef.current.focus(), 0);
    }
  }, [open, loadList]);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); btnRef.current && btnRef.current.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const markAll = async () => {
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try { await notificationsApi.markAllRead(); } catch (e) { refreshCount(); }
  };

  const onItemClick = async (n) => {
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try { await notificationsApi.markRead(n.id); } catch (e) { refreshCount(); }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const badge = unread > 9 ? '9+' : String(unread);

  return (
    <li className="action-item notif-wrap" ref={wrapRef}>
      <button
        ref={btnRef}
        className="action-button"
        aria-label="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={18} />
        {unread > 0 && <span className="notification-badge">{badge}</span>}
      </button>

      {open && (
        <div
          className="notif-panel"
          role="dialog"
          aria-label="Notifications"
          ref={panelRef}
          tabIndex={-1}
        >
          <div className="notif-head">
            <span className="notif-title">Notifications</span>
            <button className="notif-markall" onClick={markAll} disabled={unread === 0}>
              <CheckCheck size={14} /> Mark all as read
            </button>
          </div>

          <div className="notif-list">
            {loading && items.length === 0 && (
              <div className="notif-loading">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="notif-empty">
                <BellOff size={26} />
                <p>You're all caught up.</p>
              </div>
            )}
            {items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.info;
              const Icon = meta.Icon;
              return (
                <button
                  key={n.id}
                  className={`notif-item ${n.is_read ? 'read' : 'unread'}`}
                  onClick={() => onItemClick(n)}
                >
                  <span className={`notif-icon ${meta.cls}`}><Icon size={15} /></span>
                  <span className="notif-body">
                    <span className="notif-item-title">{n.title}</span>
                    {n.message && <span className="notif-item-msg">{n.message}</span>}
                    <span className="notif-item-time">{relativeTime(n.created_at)}</span>
                  </span>
                  {!n.is_read && <span className="notif-dot" aria-label="Unread" />}
                </button>
              );
            })}
          </div>

          {hasMore && (
            <div className="notif-foot">
              <button className="notif-viewall" onClick={() => { setOpen(false); navigate('/'); }}>
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default NotificationBell;
